import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Chip,
  Alert,
  AlertTitle,
  ListItemIcon,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Editor } from "@monaco-editor/react";
import { useAlert } from "../../context/AlertContext";
import { Prompt, PromptRequest } from "../../types";
import api from "../../services/api";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import GroupIcon from "@mui/icons-material/Folder";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useThemeMode } from "../../context/ThemeContext";

interface PromptFormProps {
  initialData?: Prompt;
  isEdit?: boolean;
}

// Define a specific interface for the form values
interface PromptFormValues {
  name: string;
  prompt: string;
  type: "chat";
  response_format_str: string;
  response_format_type: "json_object" | "json_schema";
  schema_name: string;
}

const PromptForm: React.FC<PromptFormProps> = ({
  initialData,
  isEdit = false,
}) => {
  const navigate = useNavigate();
  const alert = useAlert();
  const { mode } = useThemeMode();
  const [loading, setLoading] = useState<boolean>(false);
  const [updatingGroups, setUpdatingGroups] = useState<boolean>(false);
  const [originalVariables, setOriginalVariables] = useState<string[]>([]);
  const [currentVariables, setCurrentVariables] = useState<string[]>([]);
  const [addedVariables, setAddedVariables] = useState<string[]>([]);
  const [removedVariables, setRemovedVariables] = useState<string[]>([]);
  const [showVariableDialog, setShowVariableDialog] = useState<boolean>(false);
  const [availableVariableGroups, setAvailableVariableGroups] = useState<any[]>(
    [],
  );
  const [updateSummary, setUpdateSummary] = useState<{
    successful: string[];
    failed: string[];
  }>({ successful: [], failed: [] });
  const [showUpdateSummary, setShowUpdateSummary] = useState<boolean>(false);
  const [useResponseFormat, setUseResponseFormat] = useState<boolean>(
    initialData?.response_format ? true : false,
  );
  const [jsonSchemaValid, setJsonSchemaValid] = useState<boolean>(true);
  const [jsonSchemaError, setJsonSchemaError] = useState<string | null>(null);
  const [formatType, setFormatType] = useState<"json_object" | "json_schema">(
    initialData?.response_format?.type === "json_object"
      ? "json_object"
      : "json_schema",
  );

  // State for expanded sections
  const [expandedSections, setExpandedSections] = useState<{
    details: boolean;
    content: boolean;
    responseFormat: boolean;
  }>({
    details: true,
    content: true,
    responseFormat: true,
  });

  // Handle accordion expansion
  const handleAccordionChange =
    (section: keyof typeof expandedSections) =>
    (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedSections({
        ...expandedSections,
        [section]: isExpanded,
      });
    };

  // Extract variables from prompt text
  const extractVariables = (promptText: string): string[] => {
    const regex = /\{\{([^}]+)\}\}/g;
    const matches: string[] = [];
    let match;
    while ((match = regex.exec(promptText)) !== null) {
      matches.push(match[1]);
    }
    // Remove duplicates
    return matches.filter(
      (value, index, self) => self.indexOf(value) === index,
    );
  };

  // Initialize with original variables
  useEffect(() => {
    if (initialData?.prompt) {
      const variables = extractVariables(initialData.prompt);
      setOriginalVariables(variables);
      setCurrentVariables(variables);
    }

    // Set response format status based on initialData
    if (initialData?.response_format) {
      setUseResponseFormat(true);
    }

    // Fetch available variable groups
    if (isEdit) {
      fetchVariableGroups();
    }
  }, [initialData, isEdit]);

  // Fetch variable groups
  const fetchVariableGroups = async () => {
    try {
      // This is a simplified implementation - in a real app you would fetch from the actual API
      const response = await api.datasets.getAll();
      const datasets = response.data;

      // Get all variable groups from all datasets
      const allGroups: any[] = [];
      for (const dataset of datasets) {
        if (dataset.variableGroups && dataset.variableGroups.length > 0) {
          allGroups.push(
            ...dataset.variableGroups.map((group: any) => ({
              ...group,
              datasetId: dataset._id,
              datasetName: dataset.name,
            })),
          );
        }
      }

      setAvailableVariableGroups(allGroups);
    } catch (error) {
      console.error("Error fetching variable groups:", error);
    }
  };

  // Monitor prompt changes to detect variable changes
  const handlePromptChange = (value: string | undefined) => {
    const promptText = value || "";
    formik.setFieldValue("prompt", promptText);

    // Extract variables from updated prompt
    const newVariables = extractVariables(promptText);
    setCurrentVariables(newVariables);

    // Find added and removed variables
    const added = newVariables.filter((v) => !originalVariables.includes(v));
    const removed = originalVariables.filter((v) => !newVariables.includes(v));

    setAddedVariables(added);
    setRemovedVariables(removed);
  };

  // Update all variable groups
  const handleUpdateAllGroups = async () => {
    if (
      !availableVariableGroups.length ||
      (!addedVariables.length && !removedVariables.length)
    ) {
      return;
    }

    setUpdatingGroups(true);
    const successful: string[] = [];
    const failed: string[] = [];

    try {
      // Process each variable group
      for (const group of availableVariableGroups) {
        try {
          // Add new variables to the group
          if (addedVariables.length > 0) {
            for (const varName of addedVariables) {
              await api.variableGroups.createVariable(
                group.datasetId,
                group._id,
                {
                  name: varName,
                  value: "", // Default empty value
                  description: `Auto-added from prompt "${formik.values.name}"`,
                },
              );
            }
          }

          // Remove variables from the group
          if (removedVariables.length > 0) {
            for (const varName of removedVariables) {
              if (group.variables && group.variables[varName]) {
                await api.variableGroups.deleteVariable(
                  group.datasetId,
                  group._id,
                  varName,
                );
              }
            }
          }

          successful.push(group.name);
        } catch (error) {
          console.error(`Error updating group ${group.name}:`, error);
          failed.push(group.name);
        }
      }

      // Update our cached original variables
      setOriginalVariables(currentVariables);
      setAddedVariables([]);
      setRemovedVariables([]);

      // Show summary dialog
      setUpdateSummary({ successful, failed });
      setShowVariableDialog(false);
      setShowUpdateSummary(true);
    } catch (error) {
      console.error("Error updating variable groups:", error);
      alert.error("Failed to update variable groups");
    } finally {
      setUpdatingGroups(false);
    }
  };

  // Handle JSON validation
  const validateJsonSchema = (jsonString: string): boolean => {
    try {
      if (!jsonString.trim()) {
        setJsonSchemaError("JSON schema cannot be empty");
        return false;
      }

      // Parse the JSON to validate it
      const parsed = JSON.parse(jsonString);

      // For JSON Schema format, we only validate the schema structure itself
      // since we're only showing the schema part to the user, not the wrapper
      setJsonSchemaError(null);
      return true;
    } catch (error) {
      setJsonSchemaError((error as Error).message);
      return false;
    }
  };

  // Handle response format change
  const handleResponseFormatChange = (value: string | undefined) => {
    const newValue = value || "";
    formik.setFieldValue("response_format_str", newValue);
    setJsonSchemaValid(validateJsonSchema(newValue));
  };

  // Extract schema content from the full response format object
  // This removes the wrapper structure to focus only on the schema
  const extractSchemaContent = (responseFormat: any): string => {
    if (!responseFormat) return "{}";

    try {
      // If it has json_schema.schema, extract that
      if (responseFormat.json_schema?.schema) {
        return JSON.stringify(responseFormat.json_schema.schema, null, 2);
      }
      // Otherwise just use the entire object as schema
      return JSON.stringify(responseFormat, null, 2);
    } catch (error) {
      console.error("Error extracting schema content:", error);
      return "{}";
    }
  };

  // Define validation schema
  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    prompt: Yup.string().required("Prompt content is required"),
    type: Yup.string()
      .oneOf(["chat"], "Invalid prompt type")
      .required("Type is required"),
    response_format_str: Yup.string().test(
      "is-valid-json",
      "Invalid JSON format",
      function (value) {
        if (!useResponseFormat) return true;
        if (!value) return false;
        try {
          JSON.parse(value);
          return true;
        } catch (error) {
          return false;
        }
      },
    ),
    response_format_type: Yup.string().oneOf(
      ["json_object", "json_schema"],
      "Invalid response format type",
    ),
    schema_name: Yup.string().when("response_format_type", {
      is: "json_schema",
      then: (schema) =>
        useResponseFormat
          ? schema.required("Schema name is required for JSON Schema")
          : schema,
      otherwise: (schema) => schema,
    }),
  });

  // Initialize formik
  const formik = useFormik<PromptFormValues>({
    initialValues: {
      name: initialData?.name || "",
      prompt: initialData?.prompt || "",
      type: "chat",
      response_format_type:
        initialData?.response_format?.type === "json_object"
          ? "json_object"
          : "json_schema",
      schema_name: initialData?.response_format?.json_schema?.name || "",
      response_format_str: initialData?.response_format
        ? initialData?.response_format?.type === "json_object"
          ? "{}"
          : extractSchemaContent(initialData.response_format)
        : '{\n  "type": "object",\n  "properties": {\n    "example": {\n      "type": "string"\n    }\n  }\n}',
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        // Convert response_format string to object if enabled
        const requestData: PromptRequest = {
          name: values.name,
          prompt: values.prompt,
          type: "chat",
        };

        if (useResponseFormat) {
          if (values.response_format_type === "json_object") {
            // For JSON object, use a simple format
            requestData.response_format = { type: "json_object" };
          } else {
            // For JSON schema, parse the schema content and wrap it properly
            try {
              const schemaContent = JSON.parse(values.response_format_str);

              // Wrap the schema in the proper structure
              requestData.response_format = {
                type: "json_schema",
                json_schema: {
                  name: values.schema_name,
                  schema: schemaContent,
                },
              };
            } catch (error) {
              console.error("Error parsing JSON schema:", error);
              alert.error("Invalid JSON schema format");
              setLoading(false);
              return;
            }
          }
        }

        if (isEdit && initialData?._id) {
          await api.prompts.update(initialData._id, requestData);
          alert.success("Prompt updated successfully");

          // After successful update, ask if user wants to update variable groups
          if (addedVariables.length > 0 || removedVariables.length > 0) {
            setShowVariableDialog(true);
          } else {
            navigate("/prompts");
          }
        } else {
          await api.prompts.create(requestData);
          alert.success("Prompt created successfully");
          navigate("/prompts");
        }
      } catch (error) {
        console.error("Error saving prompt:", error);
        alert.error("Failed to save prompt");
        setLoading(false);
      }
    },
  });

  return (
    <Box component="form" onSubmit={formik.handleSubmit} noValidate>
      <Accordion
        expanded={expandedSections.content}
        onChange={handleAccordionChange("content")}
        sx={{ mb: 2, borderRadius: 2, overflow: "hidden" }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="prompt-content-content"
          id="prompt-content-header"
          sx={{ bgcolor: "background.paper" }}
        >
          <Typography variant="h6">Prompt Configuration</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 3, pt: 0 }}>
          <Divider sx={{ mb: 3 }} />

          {/* Prompt Details Section */}
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Prompt Details
          </Typography>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Prompt Name"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                variant="outlined"
                required
              />
            </Grid>
          </Grid>

          {/* Prompt Content Section */}
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            Prompt Content
          </Typography>
          {currentVariables.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2">Detected variables:</Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                {currentVariables.map((variable) => (
                  <Chip
                    key={variable}
                    label={`{{${variable}}}`}
                    size="small"
                    color="primary"
                  />
                ))}
              </Box>
            </Box>
          )}

          {(addedVariables.length > 0 || removedVariables.length > 0) && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                <Button
                  color="primary"
                  size="small"
                  onClick={() => setShowVariableDialog(true)}
                >
                  Update All Variable Groups
                </Button>
              }
            >
              <AlertTitle>Variables Changed</AlertTitle>
              {addedVariables.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2">Added variables:</Typography>
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}
                  >
                    {addedVariables.map((variable) => (
                      <Chip
                        key={variable}
                        label={`{{${variable}}}`}
                        size="small"
                        color="success"
                        icon={<AddIcon />}
                      />
                    ))}
                  </Box>
                </Box>
              )}
              {removedVariables.length > 0 && (
                <Box>
                  <Typography variant="body2">Removed variables:</Typography>
                  <Box
                    sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}
                  >
                    {removedVariables.map((variable) => (
                      <Chip
                        key={variable}
                        label={`{{${variable}}}`}
                        size="small"
                        color="error"
                        icon={<RemoveIcon />}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Alert>
          )}

          <Box
            sx={{
              height: 400,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              mb: 4,
            }}
          >
            <Editor
              language="markdown"
              value={formik.values.prompt}
              onChange={handlePromptChange}
              options={{
                minimap: { enabled: false },
                fontSize: 14,
                wordWrap: "on",
                lineNumbers: "on",
                folding: true,
                lineDecorationsWidth: 10,
                lineNumbersMinChars: 3,
              }}
            />
          </Box>
          {formik.touched.prompt && formik.errors.prompt && (
            <FormHelperText error>{formik.errors.prompt}</FormHelperText>
          )}

          {/* Response Format Section */}
          <Box
            sx={{
              mt: 4,
              mb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="subtitle1">Response Format</Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={useResponseFormat}
                  onChange={(e) => setUseResponseFormat(e.target.checked)}
                  color="primary"
                />
              }
              label="Enable"
              sx={{ m: 0 }}
            />
          </Box>

          {useResponseFormat && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Define the response format for structuring the LLM's response.
                  This is especially useful when you need responses in a
                  specific format for further processing.
                </Typography>
              </Box>

              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="response-format-type-label">
                  Response Format Type
                </InputLabel>
                <Select
                  labelId="response-format-type-label"
                  name="response_format_type"
                  value={formik.values.response_format_type}
                  onChange={(e) => {
                    const newFormatType = e.target.value as
                      | "json_object"
                      | "json_schema";
                    setFormatType(newFormatType);
                    formik.setFieldValue("response_format_type", newFormatType);

                    if (
                      newFormatType === "json_schema" &&
                      formik.values.response_format_str === "{}"
                    ) {
                      formik.setFieldValue(
                        "response_format_str",
                        '{\n  "type": "object",\n  "properties": {\n    "example": {\n      "type": "string"\n    }\n  }\n}',
                      );
                      validateJsonSchema(formik.values.response_format_str);
                    }
                  }}
                  label="Response Format Type"
                >
                  <MenuItem value="json_object">JSON Object</MenuItem>
                  <MenuItem value="json_schema">JSON Schema</MenuItem>
                </Select>
                <FormHelperText>
                  {formik.values.response_format_type === "json_object"
                    ? "Simple JSON object format that requires no configuration"
                    : "Define a JSON schema to structure the response format"}
                </FormHelperText>
              </FormControl>

              {formik.values.response_format_type === "json_schema" && (
                <>
                  <TextField
                    fullWidth
                    label="Schema Name"
                    name="schema_name"
                    value={formik.values.schema_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.schema_name &&
                      Boolean(formik.errors.schema_name)
                    }
                    helperText={
                      (formik.touched.schema_name &&
                        formik.errors.schema_name) ||
                      "A descriptive name for your JSON schema"
                    }
                    margin="normal"
                    sx={{ mb: 3 }}
                    required={useResponseFormat}
                  />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      pl: 2,
                      pr: 2,
                      pt: 1,
                      pb: 0.5,
                      borderBottom: (theme) =>
                        `1px solid ${theme.palette.divider}`,
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(0, 0, 0, 0.02)",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: "medium",
                      }}
                    >
                      JSON Schema Definition
                    </Typography>
                    {jsonSchemaValid ? (
                      <Chip
                        label="Valid JSON"
                        color="success"
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: "medium" }}
                      />
                    ) : (
                      <Chip
                        label="Invalid JSON"
                        color="error"
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: "medium" }}
                      />
                    )}
                  </Box>

                  <Box
                    sx={{
                      height: 300,
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                      mb: 2,
                    }}
                  >
                    <Editor
                      language="json"
                      value={formik.values.response_format_str}
                      onChange={handleResponseFormatChange}
                      theme={mode === "dark" ? "vs-dark" : "light"}
                      options={{
                        minimap: { enabled: false },
                        fontSize: 14,
                        wordWrap: "on",
                        lineNumbers: "on",
                        formatOnPaste: true,
                        formatOnType: true,
                      }}
                    />
                  </Box>
                  {jsonSchemaError && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{
                        display: "block",
                        backgroundColor: "rgba(244, 67, 54, 0.08)",
                        fontWeight: "medium",
                        padding: 1.5,
                        borderRadius: 1,
                        mb: 2,
                      }}
                    >
                      {jsonSchemaError}
                    </Typography>
                  )}
                </>
              )}

              {formik.values.response_format_type === "json_object" && (
                <Box
                  sx={{
                    mt: 2,
                    p: 3,
                    bgcolor: "background.paper",
                    borderRadius: 1,
                    border: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    The JSON Object format uses a simple structure and requires
                    no additional configuration. Your LLM responses will be
                    formatted as JSON objects.
                  </Typography>
                </Box>
              )}
            </>
          )}
        </AccordionDetails>
      </Accordion>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={() => navigate("/prompts")}
        >
          Cancel
        </Button>
        <LoadingButton
          type="submit"
          variant="contained"
          color="primary"
          loading={loading}
          disabled={
            useResponseFormat &&
            (!jsonSchemaValid ||
              (formik.values.response_format_type === "json_schema" &&
                !formik.values.schema_name))
          }
        >
          {isEdit ? "Update Prompt" : "Create Prompt"}
        </LoadingButton>
      </Box>

      {/* Variable Groups Update Dialog */}
      <Dialog
        open={showVariableDialog}
        onClose={() => setShowVariableDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Update All Variable Groups</DialogTitle>
        <DialogContent>
          {availableVariableGroups.length > 0 ? (
            <>
              <Typography variant="subtitle1" gutterBottom>
                The following variable groups will be updated:
              </Typography>
              <List>
                {availableVariableGroups.map((group) => (
                  <ListItem
                    key={group._id}
                    sx={{
                      borderRadius: 1,
                      mb: 1,
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <ListItemIcon>
                      <GroupIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={group.name}
                      secondary={`Dataset: ${group.datasetName || "Unknown"}`}
                    />
                  </ListItem>
                ))}
              </List>

              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                This will add {addedVariables.length} new variable
                {addedVariables.length !== 1 ? "s" : ""} and remove{" "}
                {removedVariables.length} variable
                {removedVariables.length !== 1 ? "s" : ""} from all variable
                groups.
                {removedVariables.length > 0 &&
                  " Variables will only be removed if they exist in a group."}
              </Typography>
            </>
          ) : (
            <Alert severity="info">
              <AlertTitle>No Variable Groups Found</AlertTitle>
              Please create a variable group in the datasets section first.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowVariableDialog(false)} color="inherit">
            Cancel
          </Button>
          <LoadingButton
            onClick={handleUpdateAllGroups}
            color="primary"
            loading={updatingGroups}
            disabled={
              availableVariableGroups.length === 0 ||
              (!addedVariables.length && !removedVariables.length)
            }
          >
            Update All Groups
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Update Summary Dialog */}
      <Dialog
        open={showUpdateSummary}
        onClose={() => {
          setShowUpdateSummary(false);
          navigate("/prompts");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Variable Groups Update Summary</DialogTitle>
        <DialogContent>
          {updateSummary.successful.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" color="success.main" gutterBottom>
                Successfully Updated ({updateSummary.successful.length}):
              </Typography>
              <Box sx={{ ml: 2 }}>
                {updateSummary.successful.map((name) => (
                  <Typography key={name} variant="body2">
                    • {name}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {updateSummary.failed.length > 0 && (
            <Box>
              <Typography variant="subtitle1" color="error.main" gutterBottom>
                Failed to Update ({updateSummary.failed.length}):
              </Typography>
              <Box sx={{ ml: 2 }}>
                {updateSummary.failed.map((name) => (
                  <Typography key={name} variant="body2">
                    • {name}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowUpdateSummary(false);
              navigate("/prompts");
            }}
            color="primary"
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PromptForm;
